import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import mainLogo from '../assets/codewave-logo.png';
import { useApiData } from '../hooks/useApiData';
import { fetchServicePage } from '../repository/api';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const servicesRef = useRef(null);

  // Fetch service page data for dropdown
  const { data: servicePageData, loading: servicePageLoading } = useApiData(fetchServicePage);

  // Debug logging
  useEffect(() => {
    console.log('Navbar - Service Page Data:', servicePageData);
    console.log('Navbar - Service Page Loading:', servicePageLoading);
  }, [servicePageData, servicePageLoading]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleServicesDropdown = () => setServicesDropdownOpen(!servicesDropdownOpen);

  return (
    <NavContainer scrolled={scrolled}>
      <NavWrapper>
        <LogoContainer>
          <LogoImage src={mainLogo} alt="Codewave Technologies" onClick={() => window.location.href = '/'} />
        </LogoContainer>
        
        <MobileIcon onClick={toggleMenu}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileIcon>
        
        <NavMenu isOpen={isOpen}>
          <NavItem>
            <NavLink href="/">Home</NavLink>
          </NavItem>
          <NavItem>
            <NavLink href="/about">About Us</NavLink>
          </NavItem>
          <NavItem ref={servicesRef}>
            <ServicesNavLink 
              onClick={toggleServicesDropdown}
              onMouseEnter={() => setServicesDropdownOpen(true)}
            >
              Services <FaChevronDown size={12} style={{ marginLeft: '5px' }} />
            </ServicesNavLink>
            
            <AnimatePresence>
              {servicesDropdownOpen && (
                <ServicesDropdown
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  onMouseLeave={() => setServicesDropdownOpen(false)}
                >
                  {/* Show API service if available, otherwise show hardcoded fallback services */}
                  {servicePageData?.page_title ? (
                    <ServiceItem style={{ backgroundColor: 'rgba(32, 144, 176, 0.1)', borderLeft: '3px solid #2090B0' }}>
                      <ServiceLink href="/services/api-service" style={{ color: '#2090B0', fontWeight: '600' }}>
                        🔥 {servicePageData.page_title}
                      </ServiceLink>
                    </ServiceItem>
                  ) : (
                    <>
                      {/* Fallback services - only show when API fails */}
                      <ServiceItem>
                        <ServiceLink href="/services/bulksms">Bulk SMS</ServiceLink>
                      </ServiceItem>
                      <ServiceItem>
                        <ServiceLink href="/services/shortcode">Short Code</ServiceLink>
                      </ServiceItem>
                      <ServiceItem>
                        <ServiceLink href="/services/ussd">USSD</ServiceLink>
                      </ServiceItem>
                      <ServiceItem>
                        <ServiceLink href="/services/callcenter">Call Center & CRM</ServiceLink>
                      </ServiceItem>
                      <ServiceItem>
                        <ServiceLink href="/services/software">Software Development</ServiceLink>
                      </ServiceItem>
                      <ServiceItem>
                        <ServiceLink href="/services/integration">System Integration</ServiceLink>
                      </ServiceItem>
                      <ServiceItem>
                        <ServiceLink href="/services/payment">Payment Solutions</ServiceLink>
                      </ServiceItem>
                      <ServiceItem>
                        <ServiceLink href="/services/ivr">IVR & Voice</ServiceLink>
                      </ServiceItem>
                    </>
                  )}
                </ServicesDropdown>
              )}
            </AnimatePresence>
          </NavItem>
          <NavItem>
            <NavLink href="/pricing">Pricing</NavLink>
          </NavItem>
          <NavItem>
            <NavLink href="/sms-portal">sms Portal</NavLink>
          </NavItem>
          <NavItem>
            <NavLink href="/blog">Blogs</NavLink>
          </NavItem>
        </NavMenu>
        
        <ContactButton href="/contact" onClick={toggleMenu}>
          Contact Us
        </ContactButton>
      </NavWrapper>
      
      <AnimatePresence>
        {isOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <MobileNavItem>
              <MobileNavLink href="/" onClick={toggleMenu}>Home</MobileNavLink>
            </MobileNavItem>
            <MobileNavItem>
              <MobileNavLink href="/about" onClick={toggleMenu}>About Us</MobileNavLink>
            </MobileNavItem>
            <MobileNavItem>
              <MobileNavItemWithDropdown>
                <MobileNavLink as="div">
                  Services
                  <MobileDropdownToggle onClick={() => toggleServicesDropdown()}>
                    <FaChevronDown size={12} />
                  </MobileDropdownToggle>
                </MobileNavLink>
              </MobileNavItemWithDropdown>
              
              <AnimatePresence>
                {servicesDropdownOpen && (
                  <MobileServicesDropdown
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Show API service if available, otherwise show hardcoded fallback services */}
                    {servicePageData?.page_title ? (
                      <MobileServiceItem style={{ backgroundColor: 'rgba(32, 144, 176, 0.1)', borderLeft: '3px solid #2090B0' }}>
                        <MobileServiceLink href="/services/api-service" onClick={toggleMenu} style={{ color: '#2090B0', fontWeight: '600' }}>
                          🔥 {servicePageData.page_title}
                        </MobileServiceLink>
                      </MobileServiceItem>
                    ) : (
                      <>
                        {/* Fallback services - only show when API fails */}
                        <MobileServiceItem>
                          <MobileServiceLink href="/services/bulksms" onClick={toggleMenu}>Bulk SMS</MobileServiceLink>
                        </MobileServiceItem>
                        <MobileServiceItem>
                          <MobileServiceLink href="/services/shortcode" onClick={toggleMenu}>Short Code</MobileServiceLink>
                        </MobileServiceItem>
                        <MobileServiceItem>
                          <MobileServiceLink href="/services/ussd" onClick={toggleMenu}>USSD</MobileServiceLink>
                        </MobileServiceItem>
                        <MobileServiceItem>
                          <MobileServiceLink href="/services/callcenter" onClick={toggleMenu}>Call Center & CRM</MobileServiceLink>
                        </MobileServiceItem>
                        <MobileServiceItem>
                          <MobileServiceLink href="/services/software" onClick={toggleMenu}>Software Development</MobileServiceLink>
                        </MobileServiceItem>
                        <MobileServiceItem>
                          <MobileServiceLink href="/services/integration" onClick={toggleMenu}>System Integration</MobileServiceLink>
                        </MobileServiceItem>
                        <MobileServiceItem>
                          <MobileServiceLink href="/services/payment" onClick={toggleMenu}>Payment Solutions</MobileServiceLink>
                        </MobileServiceItem>
                        <MobileServiceItem>
                          <MobileServiceLink href="/services/ivr" onClick={toggleMenu}>IVR & Voice</MobileServiceLink>
                        </MobileServiceItem>
                      </>
                    )}
                  </MobileServicesDropdown>
                )}
              </AnimatePresence>
            </MobileNavItem>
            <MobileNavItem>
              <MobileNavLink href="/pricing" onClick={toggleMenu}>Pricing</MobileNavLink>
            </MobileNavItem>
            <MobileNavItem>
              <MobileNavLink href="/testimonials" onClick={toggleMenu}>Testimonials</MobileNavLink>
            </MobileNavItem>
            <MobileNavItem>
              <MobileNavLink href="/sms-portal" onClick={toggleMenu}>sms portal</MobileNavLink>
            </MobileNavItem>
            <MobileNavItem>
              <MobileNavLink href="/blog" onClick={toggleMenu}>Blog</MobileNavLink>
            </MobileNavItem>
            <MobileContactButton onClick={toggleMenu} href="/contact" >
              Contact Us
            </MobileContactButton>
          </MobileMenu> 
        )}
      </AnimatePresence>
    </NavContainer>
  );
};

export default Navbar;

// Styled Components
const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 80px;
  background: ${props => props.scrolled ? '#fff' : 'transparent'};
  box-shadow: ${props => props.scrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none'};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  transition: all 0.3s ease-in-out;
`;

const NavWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1400px;
  padding: 0 2rem;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  height: 56px;
  width: auto;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 44px;
  }
`;

const MobileIcon = styled.div`
  display: none;
  
  @media screen and (max-width: 1024px) {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(-100%, 60%);
    font-size: 1.8rem;
    cursor: pointer;
    color: #105090;
    z-index: 1001;
  }
`;

const NavMenu = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  text-align: center;
  
  @media screen and (max-width: 1024px) {
    display: none;
  }
`;

const NavItem = styled.li`
  height: 80px;
  display: flex;
  align-items: center;
  position: relative;
`;

const NavLink = styled.a`
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0 1rem;
  height: 100%;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 25px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: linear-gradient(135deg, #105090, #2090B0);
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: #2090B0;
    
    &:after {
      width: 70%;
    }
  }
`;

const ServicesNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  text-transform: uppercase;
`;

const ServicesDropdown = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  text-transform: uppercase;
  min-width: 220px;
  background: white;
  border-radius: 10px;
  padding: 1rem 0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 10;
  
  &:before {
    content: '';
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 20px;
    background: white;
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
    z-index: -1;
  }
`;

const ServiceItem = styled.div`
  padding: 0.5rem 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(32, 144, 176, 0.05);
  }
`;

const ServiceLink = styled.a`
  color: #105090;
  text-decoration: none;
  text-transform: uppercase;
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.3rem 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: #2090B0;
    transform: translateX(5px);
  }
`;

const ContactButton = styled.a`
  background: linear-gradient(135deg, #105090, #2090B0);
  color: white;
  text-decoration: none;
  border: none;
  text-transform: uppercase;
  border-radius: 50px;
  padding: 10px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(32, 144, 176, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(32, 144, 176, 0.4);
  }
  
  @media screen and (max-width: 1024px) {
    display: none;
  }
`;

const MobileMenu = styled(motion.div)`
  display: none;
  
  @media screen and (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    background: white;
    padding: 1.5rem;
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
    border-radius: 0 0 20px 20px;
    z-index: 999;
    max-height: 80vh;
    overflow-y: auto;
  }
`;

const MobileNavItem = styled.div`
  width: 100%;
  text-transform: uppercase;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const MobileNavItemWithDropdown = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: uppercase;
`;

const MobileNavLink = styled.a`
  color: #105090;
  text-transform: uppercase;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  
  &:hover {
    color: #2090B0;
  }
`;

const MobileDropdownToggle = styled.span`
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background-color: rgba(32, 144, 176, 0.1);
  }
`;

const MobileServicesDropdown = styled(motion.div)`
  padding-left: 1rem;
  margin-top: 0.5rem;
  overflow: hidden;
`;

const MobileServiceItem = styled.div`
  padding: 0.4rem 0;
`;

const MobileServiceLink = styled.a`
  color: #555;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 400;
  display: block;
  padding: 0.2rem 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: #2090B0;
    transform: translateX(5px);
  }
`;

const MobileContactButton = styled.a`
  background: linear-gradient(135deg, #105090, #2090B0);
  color: white;
  text-transform: uppercase;
  border: none;
  border-radius: 50px;
  width: 100%;
  text-decoration:none;
  padding: 12px;
  margin-top: 1.5rem;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(32, 144, 176, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(32, 144, 176, 0.4);
  }
`;
